/* Shared by the product page and AJAX-rendered cart drawer. */
if (!customElements.get('rd-product-recommendations')) {
  customElements.define('rd-product-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.addEventListener('click', event => {
        const opener = event.target.closest('[data-recommendation-open]');
        const closer = event.target.closest('[data-recommendation-close]');
        const previous = event.target.closest('[data-recommendation-prev]');
        const next = event.target.closest('[data-recommendation-next]');
        if (opener) this.openDialog(opener);
        if (closer) this.closeDialog(closer.closest('dialog'));
        if (previous || next) this.scrollRecommendations(previous ? -1 : 1);
        if (event.target.matches('dialog')) {
          const bounds = event.target.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDialog(event.target);
        }
        if (opener || closer || previous || next || event.target.closest('dialog')) event.stopPropagation();
      }, { signal });
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-recommendation-form]')) return;
        event.stopPropagation();
        this.addRecommendation(event);
      }, { signal });
      this.addEventListener('close', event => this.releaseDialog(event.target), { signal, capture: true });
      this.addEventListener('keydown', event => {
        // Let the native modal own Tab and Escape instead of the drawer's handlers.
        if (event.target.closest('dialog[open]')) {
          event.stopPropagation();
          return;
        }
        if (!event.target.matches('[data-recommendation-track]') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        this.scrollRecommendations(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      const track = this.querySelector('[data-recommendation-track]');
      track?.addEventListener('scroll', () => this.updateRecommendations(), { signal, passive: true });
      this.resizeObserver?.disconnect();
      if (track && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.updateRecommendations());
        this.resizeObserver.observe(track);
      }
      window.addEventListener('resize', () => this.updateRecommendations(), { signal });
      this.updateRecommendations();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
      if (this.activeDialog) this.closeDialog(this.activeDialog);
    }

    scrollRecommendations(direction) {
      const track = this.querySelector('[data-recommendation-track]');
      track?.scrollBy({ left: direction * track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    updateRecommendations() {
      const track = this.querySelector('[data-recommendation-track]');
      if (!track) return;
      this.querySelector('[data-recommendation-prev]').disabled = track.scrollLeft <= 1;
      this.querySelector('[data-recommendation-next]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    }

    openDialog(trigger) {
      const dialog = document.getElementById(trigger.dataset.recommendationOpen);
      if (!dialog || !this.contains(dialog) || dialog.open) return;
      if (this.activeDialog) this.closeDialog(this.activeDialog);
      dialog.returnFocus = trigger;
      dialog.showModal();
      dialog.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.activeDialog = dialog;
      this.observeModalPrices(dialog);
    }

    observeModalPrices(dialog) {
      this.modalPriceObserver?.disconnect();
      const prices = dialog.querySelector?.('[data-recommendation-prices]');
      if (!prices) return;
      this.modalPriceObserver = new MutationObserver(() => this.updateModalPrices(dialog));
      this.modalPriceObserver.observe(prices, { childList: true, subtree: true, characterData: true });
      window.bucksCC?.reConvert?.();
      this.updateModalPrices(dialog);
    }

    updateModalPrices(dialog) {
      dialog.querySelectorAll('[data-recommendation-price]').forEach(price => {
        const option = dialog.querySelector(`option[value="${price.dataset.recommendationPrice}"]`);
        const formattedPrice = price.textContent.trim();
        if (!option || !formattedPrice) return;
        const label = price.dataset.recommendationLabel.replace('{price}', formattedPrice);
        if (option.textContent !== label) option.textContent = label;
      });
    }

    closeDialog(dialog) {
      dialog.close();
      this.releaseDialog(dialog);
    }

    releaseDialog(dialog) {
      if (!dialog.matches('.rd-recommendations__dialog')) return;
      if (dialog.previousOverflow !== undefined) {
        document.body.style.overflow = dialog.previousOverflow;
        delete dialog.previousOverflow;
      }
      if (dialog.returnFocus?.isConnected) dialog.returnFocus.focus({ preventScroll: true });
      delete dialog.returnFocus;
      if (this.activeDialog === dialog) {
        this.modalPriceObserver?.disconnect();
        this.activeDialog = null;
      }
    }

    async addRecommendation(event) {
      event.preventDefault();
      const form = event.target;
      const button = form.querySelector('[type="submit"]');
      if (button.disabled || !form.reportValidity() || !form.elements.id.value) return;
      const dialog = form.closest('dialog');
      const error = (dialog || form.closest('.rd-product-card')).querySelector('[data-recommendation-error]');
      error.hidden = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const cart = document.querySelector('cart-drawer');
      cart?.setActiveElement(dialog?.returnFocus || button);
      try {
        const variantId = form.elements.id.value;
        const result = await this.requestCartAdd(variantId, cart);
        if (dialog) this.closeDialog(dialog);
        this.renderCart(result, cart, variantId);
      } catch (failure) {
        error.textContent = failure.message || 'Dodavanje nije uspelo. Pokušajte ponovo.';
        error.hidden = false;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    async requestCartAdd(variantId, cart) {
      const body = { items: [{ id: variantId, quantity: 1 }], sections_url: window.location.pathname };
      if (cart) body.sections = cart.getSectionsToRender().map(section => section.id);
      const response = await fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Dodavanje nije uspelo. Pokušajte ponovo.');
      return result;
    }

    renderCart(result, cart, variantId) {
      if (!cart || cart.cartType === 'page' || cart.isCartPage || !result.sections || cart.getSectionsToRender().some(section => !result.sections[section.id])) {
        window.location.assign(window.routes.cart_url);
        return;
      }
      const wasOpen = cart.isOpen;
      const shouldOpen = this.dataset.context !== 'cart' && !wasOpen;
      cart.renderContents(result, shouldOpen);
      // The card that submitted from the drawer is removed by its HTML refresh.
      if (wasOpen) cart.closeButton()?.focus({ preventScroll: true });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rd-product-recommendations', productVariantId: variantId, cartData: result });
      }
    }
  });
}
